import _ from "lodash";
import React from "react";
import { Trans } from "react-i18next";
import {
  getGenderOptions,
  getPronounsOptions,
  getYearDurationFormat,
} from "Utils";
import {
  getLanguageOptions,
  getNationalityOptions,
} from "@toddle-edu/common-utils";

import {
  getSubjectItem,
  getSubjectVariantsMemoizedV2,
} from "CourseSetup/components/CreateCourse/containers/Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import moment from "moment";
import ACLStore from "lib/aclStore";
import { Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import { getModelTypeCheckObject } from "SchoolEntityUpsertModal/modules/SchoolEntityModalUtils.js";
import {
  ENABLE_ADD_ADMIN_TO_ALL_CLASSES,
  ENABLE_ROLES_AND_PERMISSIONS_CONFIGURATION,
} from "Constants/permissionConstants";
import { getUserArchivalStatus } from "modules/CommonSelectors";
import {
  USER_IS_VISITOR,
  USER_IS_ARCHIVED_IN_SELECTED_AY,
  USER_EXISTS_IN_OTHER_AYS,
  USER_EXISTS_IN_SELECTED_AY,
  EMAIL_ALREADY_EXISTS,
  USER_NOT_PRESENT_IN_ANY_AY,
} from "./Constants";
import { ROLE_VISITOR } from "Constants/stringConstants";
import { isRnPConfigurationEnabled } from "Administrator/modules/Utils";
import { COURSE_TYPE } from "Teacher/routes/LearningCourses/modules/Utils";
import { getRegistrationCategoryOptions } from "Utils/IBISRegistrationHelpers";

const styles = {
  staffModalTabStyle: {
    maxWidth: "200px",
    textAlign: "center",
  },
};

const spaceString = " ";

const getSubjectGroupItem = ({ subjectGroup }) => {
  const { id, name } = subjectGroup || {};
  return {
    label: name,
    value: id,
  };
};

const createToast = (programmeType, modalType, label, t) => {
  const { item } = getActionItemLabelForToastMsg(modalType, t);
  return setToastMsg({
    msg: "toastMsgs:action_item_successfully_with_count",
    locale_params: [
      { key: "action", value: programmeType, isPlainText: true },
      { key: "item", value: item, isPlainText: true },
      { key: "label", value: label, isPlainText: true },
      { key: "count", value: 1, isPlainText: true },
    ],
    type: "success",
  });
};

const canAddAdminToAllClasses = ({ modalType }) => {
  const { isStaffModal } = getModelTypeCheckObject({ modalType });

  return isStaffModal && ACLStore.can(ENABLE_ADD_ADMIN_TO_ALL_CLASSES);
};

const getCurriculumWiseCountObj = ({ curriculumWiseCount, curriculumList }) => {
  if (!_.isEmpty(curriculumWiseCount)) {
    const curriculumWiseCountObj = _.reduce(
      curriculumWiseCount,
      (result, { count, curriculumProgram }) => {
        const { id: curriculumId } = curriculumProgram;
        const displaySequence = _.findIndex(curriculumList, {
          value: curriculumId,
        });

        result[curriculumId] = {
          count,
          ...curriculumProgram,
          displaySequence,
        };
        return result;
      },
      {}
    );
    return curriculumWiseCountObj;
  } else {
    return {};
  }
};

const getActionItemLabelForToastMsg = (modalType, t) => {
  let item;
  switch (modalType) {
    case "COURSE":
      item = t("common:class");
      break;
    case "LEARNING_COURSE":
      item = t("course:course");
      break;
    case "STAFF":
      item = t("common:staff");
      break;
    case "STUDENT":
      item = t("common:student");
      break;
    case "VISITOR":
      item = t("snp:visitor");
      break;
    case "SUPERVISOR":
      item = t("project:supervisor");
      break;
  }

  return { item: _.toLower(item) };
};

const getDPCourseSelectedSubjectDetails = ({
  course,
  orgSubjectVariants,
  isLearningCourse,
}) => {
  if (_.isEmpty(course)) return {};
  const selectedSubject = _.first(course.subjects);
  const selectedSubjectGroup = isLearningCourse
    ? _.get(selectedSubject, "subjectGroup", {})
    : _.first(course.subjectGroups);

  const genericTags = isLearningCourse
    ? _.get(course, "associatedLevels", [])
    : _.get(course, "genericTags", []);

  const responseLanguageTags = isLearningCourse
    ? _.get(course, "responseLanguage", [])
    : _.get(course, "genericTags", []);

  const selectedSubjectLevelIds = _.map(
    _.filter(genericTags, ({ type }) => type == "SUBJECT_LEVEL"),
    ({ id }) => {
      return id;
    }
  );

  const responseLanguages = _.head(
    _.map(
      _.filter(responseLanguageTags, ({ type }) => type == "RESPONSE_LANGUAGE"),
      ({ id, label }) => {
        return { value: id, label };
      }
    )
  );

  const subjectsList = getSubjectVariantsMemoizedV2({
    orgSubjectVariants,
    selectedSubjectDetails: {
      subjectId: _.get(selectedSubject, "id"),
      levelIds: selectedSubjectLevelIds,
      responseLanguageId: _.get(responseLanguages, "value"),
    },
  });

  const subjectGroup = _.find(
    subjectsList,
    ({ value }) => value === selectedSubjectGroup?.id
  );

  const { options: subjects } = subjectGroup || {};
  const subject =
    _.find(subjects, ({ value }) => value === selectedSubject?.id) || {};

  subject["groupId"] = selectedSubjectGroup?.id;

  const subjectLevels = _.filter(subject.levels, level => {
    return _.includes(selectedSubjectLevelIds, level.value);
  });

  return {
    subject,
    subjectLevels,
    responseLanguages,
    subjectGroupIds: getSubjectGroupItem({ subjectGroup }),
  };
};

const getInitialClassBasicDetails = ({
  entityItem: course,
  dropdownOptions,
  selectedCurriculumObj,
  orgSubjectVariants,
}) => {
  const { isDP, isMYP, isUBD } = selectedCurriculumObj || {};
  const { academicYearsList, tagsList } = dropdownOptions;

  const {
    id,
    title,
    grades,
    primaryGrade,
    sourcedId,
    learningCourse,
    profileImageData,
  } = course;
  const updatedPrimaryGrade = {
    label: _.get(primaryGrade, "name"),
    value: _.get(primaryGrade, "id"),
  };

  const tags = _.filter(tagsList, tag => {
    const existingTagIds = _.map(_.get(course, "tags", []), ({ id }) => id);
    return _.includes(existingTagIds, tag.value);
  });

  const params = {
    id,
    title,
    associatedCourse: {
      value: _.get(learningCourse, "0.id"),
      label: _.get(learningCourse, "0.title"),
    },
    profileImageData: {
      url: _.get(profileImageData, "url"),
      icon: _.get(profileImageData, "icon"),
      acronym: _.get(profileImageData, "acronym"),
      color: _.get(profileImageData, "color"),
    },
    grades: _.map(grades, ({ id, name }) => {
      return { label: name, value: id };
    }),
    sourcedId: sourcedId,
    primaryGrade: updatedPrimaryGrade,
    tags,
  };

  if (isDP) {
    const academicYears = _.map(course.academicYears, ({ id }) => {
      const academicYear = _.find(
        academicYearsList,
        academicYear => academicYear.value === id
      );
      return academicYear;
    });

    const {
      subject,
      subjectLevels,
      responseLanguages,
    } = getDPCourseSelectedSubjectDetails({
      course,
      orgSubjectVariants,
    });

    _.assign(params, {
      academicYears,
      responseLanguageIds: responseLanguages,
      subjectLevelIds: subjectLevels,
      subjectIds: subject,
      grades: params.grades[0],
    });
  }

  if (isUBD) {
    const selectedSubjects = _.get(course, "subjects", []);
    params["subjectIds"] = _.map(selectedSubjects, selectedSubject =>
      getSubjectItem(selectedSubject)
    );
  }

  if (isMYP) {
    const selectedSubject = _.first(course.subjects) || {};
    const subjectGroup = _.get(selectedSubject, "subjectGroup", {});
    const subjectItem = getSubjectItem(selectedSubject);
    params["subjectIds"] = subjectItem;
    params["subjectGroupIds"] = getSubjectGroupItem({ subjectGroup });
  }

  return params;
};

const getInitialCourseBasicDetails = ({
  t,
  entityItem: course,
  dropdownOptions,
  mode,
  selectedCurriculumObj,
  orgSubjectVariants,
}) => {
  const { isDP, isMYP, isUBD, isPYP } = selectedCurriculumObj || {};
  const { academicYearsList } = dropdownOptions;

  const {
    id,
    title: _title,
    displayName,
    code,
    primaryGrade,
    grades,
    gradingPeriods,
    associatedLevels,
    displayMetadata,
    subjectGroup,
  } = course;

  const { icon, color, acronym } = displayMetadata || {};

  const title =
    mode == "DUPLICATE" ? `${_title} (${t("common:copy")})` : _title;

  const updatedPrimaryGrade = {
    label: _.get(primaryGrade, "name"),
    value: _.get(primaryGrade, "id"),
  };

  const updatedSubjectGroup = {
    label: _.get(subjectGroup, "name"),
    value: _.get(subjectGroup, "id"),
  };

  const isEditMode = mode == "EDIT";

  const gradingPeriodsNodes = _.map(_.get(gradingPeriods, "edges", []), "node");

  const associatedTerms = _.map(
    gradingPeriodsNodes,
    ({ id, label, startDate, endDate }) => {
      return {
        value: id,
        label: `${label} (${getYearDurationFormat({ startDate, endDate })})`,
      };
    }
  );

  const params = {
    id,
    title,
    displayName,
    courseUid: isEditMode ? code : "",
    displayMetadata: {
      icon,
      color,
      acronym,
    },
    associatedTerm: associatedTerms,
    associatedLevels: _.map(associatedLevels, ({ id, label }) => {
      return {
        value: id,
        label,
      };
    }),
    primaryGrade: updatedPrimaryGrade,
    associatedSubjectGroup: updatedSubjectGroup,
    grades: _.map(grades, ({ id, name }) => {
      return { label: name, value: id };
    }),
  };

  if (isDP) {
    const courseSubjectGroup = _.get(course, "subjects.0.subjectGroup", {});
    const isCustomSubject =
      courseSubjectGroup?.type === "OTHERS" &&
      _.isEmpty(courseSubjectGroup?.globalSubjectGroup || {});
    const academicYears = _.map(course.academicYears, ({ id }) => {
      const academicYear = _.find(
        academicYearsList,
        academicYear => academicYear.value === id
      );
      return academicYear;
    });

    const dpAssociatedTerm1 = _.filter(
      gradingPeriodsNodes,
      period => period.academicYear.id === academicYears[0]?.value
    );

    const dpAssociatedTerm2 = _.filter(
      gradingPeriodsNodes,
      period => period.academicYear.id === academicYears[1]?.value
    );

    const {
      subject,
      subjectLevels,
      responseLanguages,
    } = getDPCourseSelectedSubjectDetails({
      course,
      orgSubjectVariants,
      isLearningCourse: true,
    });

    _.assign(params, {
      academicYears,
      responseLanguageIds: responseLanguages,
      subjectLevelIds: isCustomSubject
        ? _.map(associatedLevels, ({ id, label }) => {
            return {
              value: id,
              label,
            };
          })
        : subjectLevels,
      subjectIds: subject,
      grades: params.grades[0],
      dpAssociatedTerm1: dpAssociatedTerm1
        ? _.map(dpAssociatedTerm1, term => ({
            value: term.id,
            label: `${term.label} (${getYearDurationFormat({
              startDate: term.startDate,
              endDate: term.endDate,
            })})`,
          }))
        : [],
      dpAssociatedTerm2: dpAssociatedTerm2
        ? _.map(dpAssociatedTerm2, term => ({
            value: term.id,
            label: `${term.label} (${getYearDurationFormat({
              startDate: term.startDate,
              endDate: term.endDate,
            })})`,
          }))
        : [],
    });
  }

  if (isUBD) {
    const selectedSubjects = _.get(course, "subjects", []);
    params["subjectIds"] = _.map(selectedSubjects, selectedSubject =>
      getSubjectItem(selectedSubject)
    );
  }

  if (isMYP || isPYP) {
    const selectedSubject = _.first(course.subjects) || {};
    const subjectItem = getSubjectItem(selectedSubject);
    params["subjectIds"] = subjectItem;
  }

  return params;
};

const getEntityAddedItems = ({
  entityItems,
  wrappedInNode = true,
  showArchived = false,
}) => {
  const addedItems = _.reduce(
    entityItems,
    (result, entityItem) => {
      const item = wrappedInNode ? entityItem.node : entityItem;
      const isEntityArchived =
        _.isEqual(_.get(item, "type", ""), "staff") ||
        _.isEqual(_.get(item, "type", ""), "student")
          ? getUserArchivalStatus(item)
          : false;
      if (!isEntityArchived || showArchived) {
        return [...result, item];
      }
      return result;
    },
    []
  );
  const sortedData = _.orderBy(addedItems, ["isArchived"], ["asc"]);

  return { addedItems: sortedData };
};

const updateDPStudentDetailsFromUserMap = ({ userMap }) => {
  const userMapEdges = _.get(userMap, "edges", []);
  return _.map(userMapEdges, ({ node: { user, tags } }) => {
    const subjectLevel = _.head(tags);
    return { node: { ...user, subjectLevel } };
  });
};

const appendLearningCourseRoleToStaff = ({ collaborators }) => {
  return _.map(collaborators, collaborator => {
    const teacherCourseRole = _.head(collaborator.node.rnpRoleContextDetail)
      ?.role;

    return {
      node: {
        ...collaborator.node,
        learningCourseRole: {
          context: { id: collaborator.node.id },
          contextType: "LEARNING_COURSE",
          role: teacherCourseRole,
        },
      },
    };
  });
};

const appendCourseRoleToStaff = ({ collaborators }) => {
  const isRnPConfigurationEnabled = ACLStore.can(
    ENABLE_ROLES_AND_PERMISSIONS_CONFIGURATION
  );

  if (!isRnPConfigurationEnabled) return collaborators;

  return _.map(collaborators, collaborator => {
    const teacherCourseRole = _.find(
      collaborator.node.rnpRoleContextDetail,
      rnpRole => _.get(rnpRole, "contextType") === "COURSE"
    )?.role;

    return {
      node: {
        ...collaborator.node,
        courseRole: {
          context: { id: collaborator.node.id },
          contextType: "COURSE",
          role: teacherCourseRole,
        },
      },
    };
  });
};

const getInitialClassDetails = inputProps => {
  const { entityItem } = inputProps;
  const basicDetails = getInitialClassBasicDetails(inputProps);
  const initialStaffDetails = getEntityAddedItems({
    entityItems: appendCourseRoleToStaff({
      collaborators: _.get(entityItem, "collaborator.edges", []),
    }),
    showArchived: true,
  });

  const studentUserMap = _.get(entityItem, "students", []);
  const initialStudentDetails = getEntityAddedItems({
    entityItems: updateDPStudentDetailsFromUserMap({
      userMap: studentUserMap,
    }),
    showArchived: true,
  });

  return {
    BASIC_DETAILS: basicDetails,
    ADD_STUDENTS: initialStudentDetails,
    ADD_STAFF: initialStaffDetails,
  };
};

const getInitialCourseDetails = inputProps => {
  const { entityItem, mode } = inputProps;
  const basicDetails = getInitialCourseBasicDetails(inputProps);

  const initialStaffDetails = getEntityAddedItems({
    entityItems: appendLearningCourseRoleToStaff({
      collaborators: _.get(entityItem, "collaborators.edges", []),
    }),
    showArchived: true,
  });

  const classes = _.get(inputProps, "entityItem.classes", []);

  const addedClasses = _.map(
    classes,
    ({ id, title, profileImageData, sourcedId, curriculumProgram }) => {
      return {
        id,
        title,
        label: title,
        curriculumProgram,
        profileImageData,
        sourcedId,
      };
    }
  );

  const isDuplicateMode = mode === "DUPLICATE";

  return {
    BASIC_DETAILS: basicDetails,
    ADD_STAFF: initialStaffDetails,
    ADD_CLASS: {
      addedItems: isDuplicateMode ? [] : addedClasses,
    },
  };
};

const getInitialStaffBasicDetails = ({
  entityItem: staff,
  dropdownOptions,
}) => {
  const { rolesList, tagsList } = dropdownOptions;

  const tags = _.filter(tagsList, tag => {
    const existingTagIds = _.map(
      _.get(staff, "entityTags", []),
      ({ id }) => id
    );
    return _.includes(existingTagIds, tag.value);
  });
  const params = {
    id: staff.id,
    firstName: staff.firstName,
    middleName: staff.middleName,
    lastName: staff.lastName,
    prefix: staff.prefix,
    suffix: staff.suffix,
    preferredName: staff.preferredName,
    pronouns: staff.pronouns,
    email: staff.email,
    designation: _.find(
      rolesList,
      role => role.value === _.get(staff, "designation.id")
    ),
    profileImage: staff.profileImage,
    sourceId: staff.sourceId,
    ...((ACLStore.isStaffPermissionEnabled() ||
      isRnPConfigurationEnabled()) && {
      displayRole: staff.displayRole,
      displayRoleEdited: true,
    }),
    tags,
    type: staff.type,
    staffCurriculumPrograms: _.map(
      _.get(staff, "curriculumPrograms", []),
      curriculum => {
        return {
          ...curriculum,
          value: _.get(curriculum, "id"),
        };
      }
    ),
    isArchived: staff.isArchivedV2,
  };

  return params;
};

const getStaffCourseDetails = ({ courses, initialRolesAndProgrammes }) => {
  return _.map(courses, course => {
    const { genericTags } = course;
    const updatedtags = _.map(genericTags, ({ id, label, type }) => {
      return { value: id, label, type };
    });

    return {
      ...course,
      genericTags: updatedtags,
      courseRole: _.find(
        initialRolesAndProgrammes,
        role => role.context.id === course.id
      ),
    };
  });
};

const getInitialStaffDetails = inputProps => {
  const { entityItem } = inputProps;
  const basicDetails = getInitialStaffBasicDetails(inputProps);
  const initialRolesAndProgrammes = _.get(
    entityItem,
    "rnpRoleContextDetail",
    []
  );
  const initialCoursesDetails = getEntityAddedItems({
    entityItems: getStaffCourseDetails({
      courses: _.get(entityItem, "courses", []),
      initialRolesAndProgrammes,
    }),
    wrappedInNode: false,
  });

  return {
    BASIC_DETAILS: basicDetails,
    ADD_TO_CLASSES: initialCoursesDetails,
    ROLES_AND_PROGRAMMES: {
      addedItems: _.filter(
        initialRolesAndProgrammes,
        role => role.contextType !== "COURSE"
      ),
      displayRole: entityItem.displayRole,
    },
  };
};

const getInitialStudentBasicDetails = ({
  entityItem: student,
  updateSelectedCurriculum,
  dropdownOptions,
  selectedCurriculumDetails,
  resetSchoolEntityModalFilters,
  t,
}) => {
  const { yearGroupsList } = dropdownOptions || {};
  const registrationCategory = getRegistrationCategoryOptions({ t });
  const curriculumProgramId = student.curriculumPrograms?.[0]?.id;
  const curriculumPrograms = _.get(student, "curriculumPrograms", []);
  const params = {
    id: student.id,
    firstName: student.firstName,
    middleName: _.get(student, "middleName", ""),
    lastName: student.lastName,
    pronouns: student.pronouns,
    preferredName: student.preferredName,
    prefix: student.prefix,
    suffix: student.suffix,
    profileImage: student.profileImage,
    email: student.email,
    registrationCategory: _.find(registrationCategory, {
      value: student.registrationCategory,
    }),
    curriculumPrograms,
    yearGroupId: _.find(yearGroupsList, { value: student?.yearGroup?.id }),
    sourceId: student.sourceId,
    isArchived: student.isArchivedV2,
    enrollmentDate: student.enrollmentDate,
  };

  if (_.isEmpty(selectedCurriculumDetails)) {
    const curriculumDetails = _.find(curriculumPrograms, {
      id: curriculumProgramId,
    });
    resetSchoolEntityModalFilters();
    updateSelectedCurriculum(curriculumDetails);
  }

  return params;
};

const getInitialStudentPersonalDetails = ({ entityItem: student, t }) => {
  const { otherDetails, gender } = student || {};
  const { dob, languages, primaryNationality, secondaryNationality, pronouns } =
    otherDetails || {};
  const nationalityOptions = getNationalityOptions();
  const languageOptions = getLanguageOptions();

  const personalDetails = {
    gender: _.find(getGenderOptions(t), ({ value }) =>
      _.isEqual(value, gender)
    ),
    pronouns: _.find(getPronounsOptions({ t }), ({ value }) =>
      _.isEqual(value, pronouns)
    ),
    ...(dob && { dob: moment(dob) }),
    firstNationality: _.find(nationalityOptions, { value: primaryNationality }),
    secondNationality: _.find(nationalityOptions, {
      value: secondaryNationality,
    }),
    firstLanguage: _.find(languageOptions, {
      value: _.get(languages, "[0]", ""),
    }),
    secondLanguage: _.find(languageOptions, {
      value: _.get(languages, "[1]", ""),
    }),
  };

  return personalDetails;
};

const getDPStudentCourseDetails = ({ courseMap }) => {
  return _.map(courseMap, ({ course, tags }) => {
    const subjectLevel = _.head(tags);
    return { node: { ...course, subjectLevel } };
  });
};

const getInitialStudentDetails = inputProps => {
  const { entityItem, selectedCurriculumDetails } = inputProps;
  const basicDetails = getInitialStudentBasicDetails(inputProps);
  const personalDetails = getInitialStudentPersonalDetails(inputProps);
  const initialCoursesDetails = getEntityAddedItems({
    entityItems: getDPStudentCourseDetails({
      courseMap: entityItem?.courseMap,
    }),
  });

  const initialCurriculum = _.head(_.get(entityItem, "curriculumPrograms", []));
  const isCurriculumChanged = !_.isEqual(
    _.get(initialCurriculum, "id"),
    _.get(selectedCurriculumDetails, "id")
  );

  const homeRoomAdvisor = getEntityAddedItems({
    entityItems: isCurriculumChanged
      ? []
      : _.get(entityItem, "staffRelation.edges", []),
  });

  return {
    BASIC_DETAILS: basicDetails,
    PERSONAL_DETAILS: personalDetails,
    ADD_TO_CLASSES: initialCoursesDetails,
    HOMEROOM_ADVISOR: homeRoomAdvisor,
  };
};

const getInitialVisitorBasicDetails = ({ entityItem: visitor }) => {
  const { id, firstName, lastName, email } = visitor;
  return {
    id,
    firstName,
    lastName,
    email,
  };
};

const getInitialVisitorDetails = inputProps => {
  const { entityItem } = inputProps;
  const basicDetails = getInitialVisitorBasicDetails(inputProps);
  const evaluationCycles = _.get(entityItem, "snp.snpEvaluationCycles", []);
  const addedCycles = _.map(evaluationCycles, cycle => cycle);

  return {
    BASIC_DETAILS: basicDetails,
    ADD_CYCLES: { addedItems: addedCycles },
  };
};

const getInitialDetails = ({ modalType, ...rest }) => {
  switch (modalType) {
    case "COURSE":
      return getInitialClassDetails(rest);
    case "LEARNING_COURSE":
      return getInitialCourseDetails(rest);
    case "STAFF":
      return getInitialStaffDetails(rest);
    case "STUDENT":
      return getInitialStudentDetails(rest);
    case "VISITOR":
      return getInitialVisitorDetails(rest);
  }
};

const getLastTabButton2Text = ({ modalType, editTab, mode, t, label }) => {
  if (mode === "EDIT") {
    return editTab ? t("common:save_changes") : t("common:save");
  }

  if (_.isEqual(modalType, "STAFF")) {
    return t("common:add_with_label", { label: t("common:staff_member") });
  }

  if (modalType === "LEARNING_COURSE" && mode === "DUPLICATE") {
    return t("common:duplicate_with_label", { label });
  }

  return modalType === "COURSE" || modalType === "LEARNING_COURSE"
    ? t("common:create_with_label", { label })
    : t("common:add_with_label", { label });
};

const staffModalTabSuffix = ({ t, isLastTabDisabled }) => {
  return isLastTabDisabled ? (
    <Tooltip
      tooltip={t("schoolSetup:admin_added_to_all_classes")}
      placement="bottom"
      overlayInnerStyle={styles.staffModalTabStyle}
    >
      <InformationOutlined variant="subtle" size="xxx-small" disabled={true} />
    </Tooltip>
  ) : null;
};

const getModalTabs = ({
  modalType,
  t,
  mode,
  isLastTabDisabled,
  isEnrolStudentModal,
  isProCurriculum,
  isAddAdminModal,
  isAddProgrammeStaffModal,
  courseType,
}) => {
  const isEditMode = mode === "EDIT";
  switch (modalType) {
    case "COURSE":
      return courseModalTabs({ t, isEditMode, isProCurriculum });
    case "LEARNING_COURSE":
      return learningCourseModalTabs({ t, courseType });
    case "STAFF":
      return staffModalTabs({
        t,
        isEditMode,
        isLastTabDisabled,
        isAddAdminModal,
        isAddProgrammeStaffModal,
      });
    case "STUDENT":
      return studentModalTabs({ t, isEditMode, isEnrolStudentModal });
    case "VISITOR":
      return visitorModalTabs({ t, isEditMode });
  }
};

const courseModalTabs = ({ t, isEditMode, isProCurriculum }) => {
  return [
    {
      value: "BASIC_DETAILS",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("common:basic_details") })
        : t("common:basic_details"),
      isFirst: true,
    },
    ...(isProCurriculum
      ? []
      : [
          {
            value: "ADD_STUDENTS",
            label: isEditMode
              ? t("common:edit_with_label", {
                  label: t("common:student_plural"),
                })
              : t("common:add_with_label", {
                  label: t("common:student_plural"),
                }),
          },
        ]),
    {
      value: "ADD_STAFF",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("common:staff") })
        : t("common:add_with_label", { label: t("common:staff") }),
      isLast: true,
    },
  ];
};

const learningCourseModalTabs = ({ t, courseType }) => {
  const isBlueprintCourse = courseType === COURSE_TYPE.BLUEPRINT;

  const tabs = [
    {
      value: "BASIC_DETAILS",
      label: t("common:basic_details"),
      isFirst: true,
    },
    {
      value: "ADD_STAFF",
      label: t("common:add_with_label", {
        label: t("common:staff_members_plural"),
      }),
      isLast: isBlueprintCourse,
    },
  ];

  if (!isBlueprintCourse) {
    tabs.push({
      value: "ADD_CLASS",
      label: t("common:add_with_label", { label: t("common:class_plural") }),
      isLast: true,
    });
  }

  return tabs;
};

const staffModalTabs = ({
  t,
  isEditMode,
  isLastTabDisabled,
  isAddAdminModal,
  isAddProgrammeStaffModal,
}) => {
  return _.filter(
    [
      {
        value: "BASIC_DETAILS",
        label: isEditMode
          ? t("common:edit_with_label", { label: t("common:basic_details") })
          : t("common:basic_details"),
        isFirst: true,
        visible: true,
      },
      {
        value: "ROLES_AND_PROGRAMMES",
        label: isEditMode
          ? t("common:edit_with_label", {
              label: t("common:role_plural"),
            })
          : t("project:assign_with_label", {
              user: t("common:role_plural"),
            }),
        visible:
          isRnPConfigurationEnabled() &&
          (isAddAdminModal || isAddProgrammeStaffModal || isEditMode),
      },
      {
        value: "ADD_TO_CLASSES",
        label: isEditMode
          ? t("common:edit_with_label", { label: t("common:class_plural") })
          : t("schoolSetup:add_to_classes"),
        isLast: true,
        disabled: isLastTabDisabled,
        suffix: staffModalTabSuffix({ t, isLastTabDisabled }),
        visible: true,
      },
    ],
    "visible"
  );
};

const studentModalTabs = ({ t, isEditMode, isEnrolStudentModal }) => {
  const enrolStudentModalTabs = [
    {
      value: "BASIC_DETAILS",
      label: t("common:select_year_group"),
      isFirst: true,
    },
    {
      value: "ADD_TO_CLASSES",
      label: t("schoolSetup:add_to_classes"),
    },
    {
      value: "HOMEROOM_ADVISOR",
      label: t("project:assign_with_label", {
        user: t("attendance:homeroom_advisor"),
      }),
      isLast: true,
    },
  ];
  const studentModalTabs = [
    {
      value: "BASIC_DETAILS",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("common:basic_details") })
        : t("common:basic_details"),
      isFirst: true,
    },
    {
      value: "PERSONAL_DETAILS",
      label: isEditMode
        ? _.capitalize(
            t("common:edit_with_label", {
              label: t("teacherHomePage:personal_details"),
            })
          )
        : _.capitalize(t("teacherHomePage:personal_details")),
    },
    {
      value: "ADD_TO_CLASSES",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("common:class_plural") })
        : t("schoolSetup:add_to_classes"),
    },
    {
      value: "HOMEROOM_ADVISOR",
      label: isEditMode
        ? t("common:edit_with_label", {
            label: t("attendance:homeroom_advisor"),
          })
        : t("project:assign_with_label", {
            user: t("attendance:homeroom_advisor"),
          }),
      isLast: true,
    },
  ];

  return isEnrolStudentModal ? enrolStudentModalTabs : studentModalTabs;
};

const visitorModalTabs = ({ t, isEditMode }) => {
  return [
    {
      value: "BASIC_DETAILS",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("common:basic_details") })
        : t("common:basic_details"),
      isFirst: true,
    },
    {
      value: "ADD_CYCLES",
      label: isEditMode
        ? t("common:edit_with_label", { label: t("snp:cycle_plural") })
        : t("snp:add_to_cycles"),
      isLast: true,
    },
  ];
};

const DIALOGUE_INFO = {
  INVALID_UID: {
    modalTitle: ({ t, modalType }) => {
      return modalType === "COURSE"
        ? t("schoolSetup:class_id_already_exists_error_msg")
        : t("schoolSetup:uid_already_exists_error_msg");
    },

    modalBody: ({ t, entity, basicDetails, modalType }) => {
      return modalType === "COURSE"
        ? t("schoolSetup:class_id_already_exists_modal_body", {
            entity,
            id: basicDetails?.sourcedId,
          })
        : t("schoolSetup:uid_already_exists_modal_body", {
            entity,
            id: basicDetails?.sourceId,
          });
    },
  },
  INVALID_EMAIL: {
    modalTitle: ({ t, entity }) => {
      return t("schoolSetup:entity_with_email_already_exists", { entity });
    },
    modalBody: ({ t, entity, basicDetails }) => {
      return t("schoolSetup:email_already_exists_modal_body", {
        entity,
        email: basicDetails?.email,
      });
    },
  },
  INVALID_DISPOSABLE_EMAIL: {
    modalTitle: ({ t, entity }) => {
      return t("schoolSetup:entity_with_disposable_email_not_allowed", {
        entity,
      });
    },
    modalBody: ({ t, entity, basicDetails }) => {
      return t("schoolSetup:disposable_email_modal_body", {
        entity,
        email: basicDetails?.email,
      });
    },
  },
  MULTI_ACCESS_NOT_FOUND: {
    modalTitle: () => {
      return "Multi-access identity not found";
    },
    modalBody: ({ basicDetails }) => {
      return `No multi-access identity found for ${basicDetails?.email}. Please ensure the email is registered.`;
    },
  },
};

const ERROR_TYPE_MAPPING = {
  "TD/UPDUSR/001": "INVALID_UID",
  "TD/UPDUSR/002": "INVALID_EMAIL",
  "TD/UPDUSR/003": "INVALID_DISPOSABLE_EMAIL",
  "TD/UPDCRS/001": "INVALID_UID",
  "TD/INSCRS/001": "INVALID_UID",
  "TD/SIGNUP/002": "INVALID_DISPOSABLE_EMAIL",
  "TD/SIGNUP/003": "MULTI_ACCESS_NOT_FOUND",
};

const getGradeYearGroups = ({ grades, yearGroups }) => {
  return _.reduce(
    yearGroups,
    (result, yearGroup) => {
      const {
        grade: { id },
      } = yearGroup;
      const isGradeSelected = _.isArray(grades)
        ? _.find(grades, { value: id })
        : _.isEqual(grades.value, id);
      if (isGradeSelected) {
        result.push(yearGroup.value);
      }
      return result;
    },
    []
  );
};

const getCategoryWiseAssessmentsMemoized = _.memoize(
  ({ academicCriteriaSets }) => {
    const categoryWiseAssessments = [];
    _.forEach(academicCriteriaSets, academicCriteriaSet => {
      _.forEach(
        _.get(academicCriteriaSet, "academicCriteriaValues", []),
        academicCriteriaValue => {
          const updatedAcademicCriteriaValue = _.cloneDeep(
            academicCriteriaValue
          );
          const assignments = _.get(academicCriteriaValue, "assignments", []);
          const assessments = _.get(academicCriteriaValue, "assessments", []);

          if (!_.isEmpty(assignments) || !_.isEmpty(assessments)) {
            categoryWiseAssessments.push({
              ...updatedAcademicCriteriaValue,
              assignments,
              assessments,
              academicCriteriaSetId: _.get(academicCriteriaSet, "id"),
              isShowGradeBoundaries: _.get(
                academicCriteriaSet,
                "isShowGradeBoundaries",
                false
              ),
            });
          }
        }
      );
    });
    return categoryWiseAssessments;
  },
  params => JSON.stringify(params)
);

export const getEmailAlreadyExistsErrorModalObject = ({
  errorType,
  t,
  userDetails,
  styleClasses,
  handleUserClick,
}) => {
  const {
    email,
    staffName,
    academicYearDateRange,
    userType,
    sourceId = "",
    isUIDAlreadyExists,
    canViewStudentProfile,
  } = userDetails;

  let userLabelForModalHeading = "";
  let userLabelForModalContent = "";

  switch (userType) {
    case "STAFF": {
      userLabelForModalHeading = t("common:staff");
      userLabelForModalContent = t("schoolSetup:staff_member");
      break;
    }
    case "STUDENT": {
      userLabelForModalHeading = t("common:student");
      userLabelForModalContent = userLabelForModalHeading;
      break;
    }
  }

  const userLabelForModalHeadingLowerCase = t("common:label_lowercase", {
    label: userLabelForModalHeading,
  });

  const userLabelForModalContentLowerCase = t("common:label_lowercase", {
    label: userLabelForModalContent,
  });

  const shouldShowViewUserLink =
    userType !== "STUDENT" || canViewStudentProfile;

  const EMAIL_ALREADY_EXISTS_ERROR_MODAL_TYPES = {
    [USER_IS_VISITOR]: {
      header: t("schoolSetup:user_with_email_already_exists_error_header", {
        user: t("schoolSetup:visitor"),
      }),
      bodyComponent: (
        <div className="text-body">
          <Trans
            i18nKey={
              shouldShowViewUserLink
                ? "schoolSetup:email_already_exists_for_the_visitor_error_body"
                : "schoolSetup:email_already_exists_for_the_visitor_error_body_no_link"
            }
          >
            A visitor already exists with the email `{{ email }}`{spaceString}
            {shouldShowViewUserLink ? (
              <div
                className={styleClasses.viewUserTextStyle}
                onClick={handleUserClick}
                role="button"
                tabIndex="0"
                onKeyPress={e => {
                  if (e.key === "Enter" || e.key === " ") handleUserClick();
                }}
              >
                {{ viewUser: t("schoolSetup:view_user") }}
              </div>
            ) : null}
            In order to use this email for a staff member remove the email id
            from the visitor and then create a staff member with the email ID
            Please enter a unique email ID for {{ staffName }}
          </Trans>
        </div>
      ),
      closeButtonText: t("schoolSetup:go_back_to_basic_details"),
    },
    [USER_IS_ARCHIVED_IN_SELECTED_AY]: {
      header: t(
        "schoolSetup:user_with_email_or_UID_already_exists_error_header",
        {
          user: userLabelForModalHeadingLowerCase,
          type: isUIDAlreadyExists
            ? t("common:uid")
            : t("schoolSetup:email_ID"),
        }
      ),
      bodyComponent: (
        <div className="text-body">
          <Trans
            i18nKey={
              shouldShowViewUserLink
                ? "schoolSetup:archived_user_email_or_uid_exists"
                : "schoolSetup:archived_user_email_or_uid_exists_no_link"
            }
          >
            An archived {{ user: userLabelForModalContentLowerCase }} already
            exists with the{spaceString}
            {{
              type: isUIDAlreadyExists
                ? t("common:uid")
                : t("common:label_lowercase", {
                    label: t("common:email"),
                  }),
            }}
            {spaceString}`
            {{ emailOrUID: isUIDAlreadyExists ? sourceId : email }}`
            {spaceString}
            {shouldShowViewUserLink ? (
              <div
                className={styleClasses.viewUserTextStyle}
                onClick={handleUserClick}
                role="button"
                tabIndex="0"
                onKeyPress={e => {
                  if (e.key === "Enter" || e.key === " ") handleUserClick();
                }}
              >
                {{ viewUser: t("schoolSetup:view_user") }}
              </div>
            ) : null}
            Please enter a unique{spaceString}
            {{
              emailIDorUID: isUIDAlreadyExists
                ? t("common:uid")
                : t("schoolSetup:email_ID"),
            }}
            {spaceString}
            for {{ staffName }}.
          </Trans>
        </div>
      ),
      closeButtonText: t("schoolSetup:go_back_to_basic_details"),
    },
    [USER_EXISTS_IN_SELECTED_AY]: {
      header: t(
        "schoolSetup:user_with_email_or_UID_already_exists_error_header",
        {
          user: userLabelForModalHeadingLowerCase,
          type: isUIDAlreadyExists
            ? t("common:uid")
            : t("schoolSetup:email_ID"),
        }
      ),
      bodyComponent: (
        <div className="text-body">
          <Trans
            i18nKey={
              shouldShowViewUserLink
                ? "schoolSetup:user_email_or_uid_already_exists"
                : "schoolSetup:user_email_or_uid_already_exists_no_link"
            }
          >
            A {{ user: userLabelForModalContentLowerCase }} already exists with
            the{spaceString}
            {{
              type: isUIDAlreadyExists
                ? t("common:uid")
                : t("common:label_lowercase", {
                    label: t("common:email"),
                  }),
            }}
            {spaceString}`
            {{ emailOrUID: isUIDAlreadyExists ? sourceId : email }}`
            {spaceString}
            {shouldShowViewUserLink ? (
              <div
                className={styleClasses.viewUserTextStyle}
                onClick={handleUserClick}
                role="button"
                tabIndex="0"
                onKeyPress={e => {
                  if (e.key === "Enter" || e.key === " ") handleUserClick();
                }}
              >
                {{ viewUser: t("schoolSetup:view_user") }}
              </div>
            ) : null}
            Please enter a unique{spaceString}
            {{
              emailIDorUID: isUIDAlreadyExists
                ? t("common:uid")
                : t("schoolSetup:email_ID"),
            }}
            {spaceString}
            for {{ staffName }}.
          </Trans>
        </div>
      ),
      closeButtonText: t("common:go_back"),
    },
    [USER_EXISTS_IN_OTHER_AYS]: {
      header: t(
        "schoolSetup:user_with_email_or_UID_already_exists_error_header",
        {
          user: userLabelForModalHeadingLowerCase,
          type: isUIDAlreadyExists
            ? t("common:uid")
            : t("schoolSetup:email_ID"),
        }
      ),
      bodyComponent: (
        <div className="text-body">
          <Trans
            i18nKey={
              "schoolSetup:user_email_or_UID_already_exists_for_previous_or_future_AY"
            }
          >
            A {{ user: userLabelForModalContentLowerCase }} already exists with
            the{spaceString}
            {{
              type: isUIDAlreadyExists
                ? t("common:uid")
                : t("common:label_lowercase", {
                    label: t("common:email"),
                  }),
            }}
            {spaceString}`
            {{ emailOrUID: isUIDAlreadyExists ? sourceId : email }}` in AY
            {spaceString}
            {{ academicYearDateRange }}
            use the button below to add the user, or go back enter a unique
            {{
              emailIDorUID: isUIDAlreadyExists
                ? t("common:uid")
                : t("schoolSetup:email_ID"),
            }}
            {spaceString}
            for {{ staffName }}.
          </Trans>
        </div>
      ),
      closeButtonText: t("common:go_back"),
      confirmButtonText: t("schoolSetup:add_user_from_other_year"),
      onClickConfirm: handleUserClick,
    },
    [USER_NOT_PRESENT_IN_ANY_AY]: {
      header: t(
        "schoolSetup:user_with_email_or_UID_already_exists_error_header",
        {
          user: userLabelForModalHeadingLowerCase,
          type: isUIDAlreadyExists
            ? t("common:uid")
            : t("schoolSetup:email_ID"),
        }
      ),
      bodyComponent: (
        <div className="text-body">
          <Trans i18nKey="schoolSetup:user_email_or_uid_already_exists_no_link">
            A {{ user: userLabelForModalContentLowerCase }} already exists with
            the{spaceString}
            {{
              type: isUIDAlreadyExists
                ? t("common:uid")
                : t("common:label_lowercase", {
                    label: t("common:email"),
                  }),
            }}
            {spaceString}`
            {{ emailOrUID: isUIDAlreadyExists ? sourceId : email }}` Please
            enter a unique{spaceString}
            {{
              emailIDorUID: isUIDAlreadyExists
                ? t("common:uid")
                : t("schoolSetup:email_ID"),
            }}
            {spaceString}
            for {{ staffName }}.
          </Trans>
        </div>
      ),
      closeButtonText: t("common:go_back"),
      confirmButtonText: t("common:edit_with_label", {
        label: t("common:user"),
      }),
      onClickConfirm: handleUserClick,
    },
  };

  return EMAIL_ALREADY_EXISTS_ERROR_MODAL_TYPES[errorType];
};

/**
 * Analyzes the response and determines the appropriate email already exists error state
 * @param {Object} params - The parameters object
 * @param {Object} params.response - The API response object
 * @param {string} params.selectedAcademicYearId - The ID of the selected academic year
 * @returns {Object|null} The email error state object or null if no condition is met
 */
export const getEmailAlreadyExistsErrorObject = ({
  response,
  selectedAcademicYearId,
}) => {
  const user = _.get(response, "data", {});
  const isVisitor = _.isEqual(_.get(user, "role", ""), ROLE_VISITOR);

  // Check if user is present in selected academic year
  const isUserPresentInSelectedAcademicYear = _.find(
    _.get(user, "academicYears", []),
    academicYear => _.get(academicYear, "id", "") === selectedAcademicYearId
  );

  // Check if user is present in other academic years
  const isUserPresentInOtherAYs = _.find(
    _.get(user, "academicYears", []),
    academicYear => !_.get(academicYear, "isCurrentAcademicYear", false)
  );

  // Check if user is unenrolled from all academic years
  const isUserNotPresentInAnyAY = _.isEmpty(_.get(user, "academicYears", []));

  // Case 1: User is a visitor
  if (isVisitor) {
    return {
      type: EMAIL_ALREADY_EXISTS,
      data: {
        errorType: USER_IS_VISITOR,
        user,
      },
    };
  }

  // Case 2: User exists in the selected academic year
  if (isUserPresentInSelectedAcademicYear) {
    const isArchived = _.get(user, "isArchivedV2", false);
    return {
      type: EMAIL_ALREADY_EXISTS,
      data: {
        errorType: isArchived
          ? USER_IS_ARCHIVED_IN_SELECTED_AY
          : USER_EXISTS_IN_SELECTED_AY,
        user,
      },
    };
  }

  // Case 3: User exists in other academic years
  if (isUserPresentInOtherAYs) {
    return {
      type: EMAIL_ALREADY_EXISTS,
      data: {
        errorType: USER_EXISTS_IN_OTHER_AYS,
        user,
      },
    };
  }

  // Case 4: User is not present in any academic year
  if (isUserNotPresentInAnyAY) {
    return {
      type: EMAIL_ALREADY_EXISTS,
      data: {
        errorType: USER_NOT_PRESENT_IN_ANY_AY,
        user,
      },
    };
  }

  // No condition met
  return null;
};

const getAssociatedListOptions = subjects => {
  // Find unique levels across all subjects and return them as options
  const subjectsList = Array.isArray(subjects) ? subjects : [subjects];
  const listOptions = _(subjectsList)
    .map(item => item.associatedLevels || [])
    .thru(levelLists => _.intersectionBy(...levelLists, "id"))
    .map(props => {
      const { id, label } = props;
      return { value: id, label, id };
    })
    .value();

  return listOptions;
};

export {
  getModalTabs,
  getLastTabButton2Text,
  getInitialDetails,
  DIALOGUE_INFO,
  getGenderOptions,
  ERROR_TYPE_MAPPING,
  getGradeYearGroups,
  getActionItemLabelForToastMsg,
  createToast,
  canAddAdminToAllClasses,
  getCurriculumWiseCountObj,
  getCategoryWiseAssessmentsMemoized,
  getAssociatedListOptions,
};
